import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useFaceIO } from '@/hooks/useFaceIO';
import { ScanFace, CheckCircle2, AlertCircle, Camera, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function LandingPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { faceioInstance, isInitialized } = useFaceIO();
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [livenessDetected, setLivenessDetected] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
        setCameraError(false);
      }
    } catch (error) {
      setCameraError(true);
      toast.error('Camera access denied');
      if (process.env.NODE_ENV === 'development') {
        console.error('Camera access error:', error);
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const handleFaceAuth = async () => {
    if (!faceioInstance) {
      await handleMockAuth();
      return;
    }

    try {
      setIsScanning(true);
      await startCamera();

      const userData = await faceioInstance.authenticate({ 
        locale: 'auto',
        permissionTimeout: 30,
        idleTimeout: 30
      });
      
      setLivenessDetected(true);
      setTimeout(() => {
        setScanSuccess(true);
        toast.success('Authenticated!');

        setTimeout(() => {
          login({
            facialId: userData.facialId,
            payload: userData.payload,
            timestamp: new Date().toISOString(),
          });
          stopCamera();
        }, 800);
      }, 1500);
    } catch (errCode) {
      stopCamera();
      setIsScanning(false);
      setScanSuccess(false);
      setLivenessDetected(false);
      
      if (errCode === 'UNRECOGNIZED_FACE') {
        toast.error('Face not recognized. Enrolling new user...');
        await handleEnrollment();
      } else if (errCode === 'PERMISSION_REFUSED') {
        toast.error('Camera permission denied');
      } else {
        toast.error(`Authentication failed`);
      }
    }
  };

  const handleEnrollment = async () => {
    try {
      setIsScanning(true);
      await startCamera();

      const userInfo = await faceioInstance.enroll({
        locale: 'auto',
        payload: {
          userId: `user_${Date.now()}`,
          enrolledAt: new Date().toISOString(),
        },
      });

      setLivenessDetected(true);
      setTimeout(() => {
        setScanSuccess(true);
        toast.success('New user enrolled!');

        setTimeout(() => {
          login({
            facialId: userInfo.facialId,
            enrolled: true,
            timestamp: new Date().toISOString(),
          });
          stopCamera();
        }, 800);
      }, 1500);
    } catch (errCode) {
      stopCamera();
      setIsScanning(false);
      toast.error(`Enrollment failed: ${errCode}`);
    }
  };

  const handleMockAuth = async () => {
    setIsScanning(true);
    await startCamera();

    setTimeout(() => {
      setLivenessDetected(true);
      setTimeout(() => {
        setScanSuccess(true);
        toast.success('Demo authenticated!');

        setTimeout(() => {
          login({
            facialId: 'demo-user-' + Date.now(),
            userId: `demo_${Date.now()}`,
            demoMode: true,
            timestamp: new Date().toISOString(),
          });
          stopCamera();
        }, 800);
      }, 1500);
    }, 1500);
  };

  return (
    <div className="min-h-screen w-full bg-white overflow-x-hidden flex items-center justify-center" data-testid="landing-page">
      <div className="flex flex-col items-center justify-center p-4 pb-16">
        {/* Auth Card */}
        <div 
          className="w-full max-w-md mx-auto flex flex-col items-center p-6 gap-6 bg-white border border-zinc-200 rounded-xl"
          data-testid="auth-card"
        >
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Shield className="text-blue-600" size={28} strokeWidth={2} />
              <h1 className="text-3xl font-black tracking-tighter text-zinc-950">
                TRACK CC
              </h1>
            </div>
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-500">
              Biometric Security
            </p>
          </div>

          <div 
            className={`relative w-40 h-40 rounded-full overflow-hidden flex items-center justify-center bg-zinc-50 border-4 transition-all duration-500 ${
              scanSuccess 
                ? 'border-emerald-500' 
                : isScanning 
                ? 'border-blue-500 pulse-border' 
                : 'border-zinc-200'
            }`}
            data-testid="face-scanner"
          >
            {cameraActive ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="camera-feed"
                />
                {isScanning && !scanSuccess && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent scan-beam shadow-lg shadow-blue-500/50" />
                    {livenessDetected && (
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                        LIVE
                      </div>
                    )}
                  </div>
                )}
                {scanSuccess && (
                  <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/10 backdrop-blur-sm">
                    <CheckCircle2 size={48} className="text-emerald-500" strokeWidth={2} />
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-1.5">
                {cameraError ? (
                  <>
                    <AlertCircle size={36} className="text-red-500" strokeWidth={1.5} />
                    <p className="text-[10px] text-zinc-600 text-center px-3">Camera denied</p>
                  </>
                ) : (
                  <>
                    <Camera size={36} className="text-zinc-400" strokeWidth={1.5} />
                    <p className="text-[10px] text-zinc-500">Ready to scan</p>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="w-full flex flex-col gap-2">
            <Button
              onClick={handleFaceAuth}
              disabled={isScanning}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl"
              data-testid="start-scan-button"
            >
              {isScanning ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {livenessDetected ? 'Verifying...' : 'Scanning...'}
                </>
              ) : (
                <>
                  <ScanFace className="mr-2" size={18} />
                  Authenticate
                </>
              )}
            </Button>

            {cameraError && (
              <Button
                onClick={handleMockAuth}
                variant="outline"
                className="w-full h-9 border-zinc-300 text-zinc-700 hover:bg-zinc-100 rounded-xl text-sm"
                data-testid="mock-login-button"
              >
                Demo Login
              </Button>
            )}
          </div>

          <div className="text-center">
            <p className="text-xs text-zinc-500">
              {!faceioInstance ? (
                <span className="text-amber-600 font-medium">Demo Mode</span>
              ) : (
                'Session-based authentication'
              )}
            </p>
            <p className="text-[10px] text-zinc-400 mt-1">
              Login required every session
            </p>
          </div>
        </div>

        {/* Disclaimer Banner */}
        <div className="w-full max-w-md mx-auto mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="disclaimer-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-800">Simulation Only</p>
              <p className="text-[10px] text-amber-700 mt-0.5">
                This is a working demo. Do not upload actual production data. This application does not address all applicable financial regulations (PCI-DSS, GDPR, etc.).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
